import { useEffect } from 'react';
import { compose } from 'redux';
import { useRecoilState } from 'recoil';
import { useTranslation } from 'react-i18next';
import { homePageVisitAnalyticsSent } from '../../../models/home-page/recoil-state';
import { channelSelectors } from '../../../models/channels';
import withRerenderPrevent from '../../../common/with-rerender-prevent';
import useScreenTitle from '../../../common/use-screen-title';
import { trackHomeLoad } from '../../../models/analytics';
import useFeatureFlag from '../../../common/use-feature-flag';
import { Spinner } from '../../../components/ui';
import HomeFeeds from './home-feeds';
import './home.scss';
import { Feature } from '../../../models/features/features';
import HomepageShortcuts from '../../../components/v2/homepage-shortcuts/homepage-shortcuts';
import { HomeSection } from '../../../components/v2/home-section';
import { usePatronSelector } from '../../../common/use-patron-selector';
import { useHomeFeedSectionsQuery } from '../../../common/queries/use-home-feed-sections-query';

/**
 * Home component for displaying home page content
 */
const Home = () => {
  useScreenTitle();
  const recommendedChannelId = usePatronSelector((state) =>
    channelSelectors.getChannelIdFirstUniqRecommended(state)
  );
  const newestChannelId = usePatronSelector((state) =>
    channelSelectors.getChannelIdFirstUniqNewest(state)
  );
  const popularChannelId = usePatronSelector((state) =>
    channelSelectors.getChannelIdFirstUniqPopular(state)
  );

  const [visitedAnalyticsSent, setVisitedAnalyticsSent] = useRecoilState(
    homePageVisitAnalyticsSent
  );

  useEffect(() => {
    if (!visitedAnalyticsSent) {
      trackHomeLoad();
      setVisitedAnalyticsSent(true);
    }
  }, [visitedAnalyticsSent, setVisitedAnalyticsSent]);

  const { t } = useTranslation();

  // Feature flags
  const newShortcutsEnabled = useFeatureFlag(Feature.NEW_SHORTCUTS_ENABLED);
  const newestTopicEnabled = useFeatureFlag(Feature.NEWEST_TOPIC_ENABLED);
  const recommendedTopicEnabled = useFeatureFlag(
    Feature.RECOMMENDED_TOPIC_ENABLED
  );
  const popularTopicEnabled = useFeatureFlag(Feature.POPULAR_TOPIC_ENABLED);
  const newTopicsString = useFeatureFlag(Feature.CHANNELS_TO_TOPICS_ENABLED);

  // Translation keys
  const recommendedChannelTitle = newTopicsString
    ? 'landing.recommended_topic'
    : 'landing.recommended_channel';
  const newestChannelTitle = newTopicsString
    ? 'landing.newest_topic'
    : 'landing.newest_channel';
  const mostPopularChannelString = newTopicsString
    ? 'landing.most_popular_topic'
    : 'landing.most_popular_channel';

  const { isLoading: feedSectionsIsFetching } = useHomeFeedSectionsQuery();

  return (
    <div className="home-v1 page">
      {/* screen reader only */}
      <h1 className="sr-only">{t('nav_links.home')}</h1>

      {/* Homepage Shortcuts Section - loads independently */}
      {newShortcutsEnabled && <HomepageShortcuts />}

      {/* Home Feeds Section - depends on feedSectionsIsFetching */}
      <div className="home__feeds-container">
        {feedSectionsIsFetching ? <Spinner /> : <HomeFeeds />}
      </div>

      {/* Channels Section - each channel loads independently */}
      <div className="home__channels">
        <div className="container">
          {recommendedTopicEnabled && recommendedChannelId && (
            <>
              <HomeSection
                title={t(recommendedChannelTitle)}
                channelId={String(recommendedChannelId)}
                minLength={1}
                maxLength={4}
                analyticsData={{ location: 'home_channel_recommended' }}
              />
            </>
          )}

          {newestTopicEnabled && newestChannelId && (
            <>
              <HomeSection
                title={t(newestChannelTitle)}
                channelId={newestChannelId}
                minLength={1}
                maxLength={4}
                analyticsData={{ location: 'home_channel_newest' }}
              />
            </>
          )}

          {popularTopicEnabled && popularChannelId && (
            <>
              <HomeSection
                title={t(mostPopularChannelString)}
                channelId={popularChannelId}
                minLength={1}
                maxLength={4}
                analyticsData={{ location: 'home_channel_popular' }}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default compose(withRerenderPrevent)(Home);
